import React from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  Truck, AlertTriangle, RotateCw, CheckCircle2, ExternalLink, Printer, Play, Square, SkipForward,
} from "lucide-react";
import { FASCE_LABEL, FASCE_VISITA_LABEL, FASCE_COLOR, STATI_GIRO, STATO_GIRO_LABEL, STATO_GIRO_COLOR, formatNumber } from "../../lib/constants";
import { SortableStop, SkippedStop } from "./StopItems";

export function MezzoBlock({
  mezzoData, doneIds, onReorder, onReoptimize, onRegister, onChangeStato,
  onChangeMezzo, onChangeTrasportatore, onOpenMaps, onPrint, onSkip, onRestore,
  onStopOverride, onCreaRecuperoSaltata, dataGiro,
  allMezzi, allTrasportatori = [], isCarrier = false, disableReorder = false,
  hideInlineAssegnazione = false,
}) {
  const {
    mezzo, fermate, fermateSaltate = [], fasce, totalKm, isOverride, mezzoPredefinito,
    stato, giroId, trasportatore, trasportatoreAbituale, isTraspOverride,
  } = mezzoData;
  const color = mezzo?.colore || "#94a3b8";
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const stopsById = Object.fromEntries(fermate.map((s) => [s.id, s]));
  const groupedFasce = (fasce || []).map((f) => ({
    ...f, stopsResolved: f.stops.map((id) => stopsById[id]).filter(Boolean),
  }));
  const totalRitirati = fermate.filter((s) => doneIds.has(s.id)).length;
  const cancelled = stato === "ANNULLATO";
  const chiuso = stato === "COMPLETATO" || stato === "ANNULLATO";

  return (
    <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={`mezzo-block-${mezzo?.id}`}>
      <div className="p-4 border-b" style={{ backgroundColor: `${color}10`, borderColor: `${color}30` }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: color }}>
            <Truck size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{mezzo?.nome || "Senza mezzo"}</div>
              {isOverride && <Badge className="bg-[#F57C00] text-white text-[10px]" data-testid={`override-badge-${giroId}`}>OVERRIDE OGGI</Badge>}
              {isTraspOverride && <Badge className="bg-[#1565C0] text-white text-[10px]" data-testid={`trasp-override-badge-${giroId}`}>AUTISTA SOST.</Badge>}
              <Badge style={{ backgroundColor: STATO_GIRO_COLOR[stato], color: "#fff" }} className="text-[10px]">{STATO_GIRO_LABEL[stato]}</Badge>
            </div>
            <div className="text-xs text-slate-600">
              {mezzoData.zona} · {fermate.length} fermate · {formatNumber(totalKm)} km
              {trasportatore && (
                <span className="ml-1 text-slate-500">· Autista: <span className="font-medium text-slate-700">{trasportatore.nome} {trasportatore.cognome}</span></span>
              )}
            </div>
          </div>
        </div>
        <div className={`grid ${isCarrier || hideInlineAssegnazione ? "grid-cols-1" : "grid-cols-3"} gap-2 text-xs`}>
          {!isCarrier && !hideInlineAssegnazione && (
            <div>
              <Label className="text-[10px] uppercase">Mezzo oggi</Label>
              <Select value={mezzo?.id || ""} onValueChange={(v) => onChangeMezzo(giroId, v)}>
                <SelectTrigger data-testid={`override-select-${giroId}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allMezzi.filter((m) => m.attivo).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: m.colore }} />
                        {m.nome}{mezzoPredefinito?.id === m.id ? " (predefinito)" : ""}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isCarrier && !hideInlineAssegnazione && (
            <div>
              <Label className="text-[10px] uppercase">Autista oggi</Label>
              <Select
                value={trasportatore?.id || "_none_"}
                onValueChange={(v) => onChangeTrasportatore(giroId, v === "_none_" ? null : v)}
              >
                <SelectTrigger data-testid={`trasportatore-select-${giroId}`} className="h-9 text-xs"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">Nessuno</SelectItem>
                  {allTrasportatori.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.nome} {t.cognome}{trasportatoreAbituale?.id === t.id ? " (predefinito)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className={isCarrier || hideInlineAssegnazione ? "col-span-1" : ""}>
            <Label className="text-[10px] uppercase">Stato giro</Label>
            <Select value={stato} onValueChange={(v) => onChangeStato(giroId, v)} disabled={isCarrier && stato === "ANNULLATO"}>
              <SelectTrigger data-testid={`stato-select-${giroId}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATI_GIRO.filter((s) => !isCarrier || s !== "ANNULLATO").map((s) => <SelectItem key={s} value={s}>{STATO_GIRO_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {stato === "PIANIFICATO" && (
            <Button size="sm" onClick={() => onChangeStato(giroId, "IN_CORSO")} className="bg-[#F57C00] hover:bg-[#E65100] text-white min-h-[40px] text-xs" data-testid={`start-${giroId}`}>
              <Play size={14} className="mr-1" /> Inizia giro
            </Button>
          )}
          {stato === "IN_CORSO" && (
            <Button size="sm" onClick={() => onChangeStato(giroId, "COMPLETATO")} className="bg-[#1B5E20] hover:bg-[#144818] text-white min-h-[40px] text-xs" data-testid={`finish-${giroId}`}>
              <Square size={14} className="mr-1" /> Termina giro
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onOpenMaps(mezzoData)} className="min-h-[40px] text-xs" data-testid={`open-maps-${giroId}`}>
            <ExternalLink size={14} className="mr-1" /> Apri in Mappe
          </Button>
          <Button size="sm" variant="outline" onClick={() => onPrint(mezzoData)} className="min-h-[40px] text-xs" data-testid={`print-${giroId}`}>
            <Printer size={14} className="mr-1" /> Stampa foglio
          </Button>
          {!isCarrier && (
            <Button size="sm" variant="outline" onClick={() => onReoptimize(giroId)} className="min-h-[40px] text-xs ml-auto" data-testid={`reopt-${giroId}`}>
              <RotateCw size={14} className="mr-1" /> Ricalcola
            </Button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3" style={{ opacity: cancelled ? 0.5 : 1 }}>
        {totalRitirati > 0 && (
          <div className="text-xs text-[#1B5E20] font-semibold">
            <CheckCircle2 size={14} className="inline -mt-0.5 mr-1" /> {totalRitirati} ritirati su {fermate.length}
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            for (const f of groupedFasce) {
              const ids = f.stopsResolved.map((s) => s.id);
              const oldIdx = ids.indexOf(active.id);
              const newIdx = ids.indexOf(over.id);
              if (oldIdx !== -1 && newIdx !== -1) {
                onReorder(giroId, f.fascia, arrayMove(ids, oldIdx, newIdx));
                return;
              }
            }
          }}
        >
          {groupedFasce.map((f, fi) => {
            if (f.stopsResolved.length === 0) return null;
            // B5.4 — etichetta difensiva: prima cerca tra le fasce VISITA (zone),
            // poi tra le legacy; fallback sul codice grezzo.
            const fascLabel = FASCE_VISITA_LABEL[f.fascia] || FASCE_LABEL[f.fascia] || f.fascia;
            // Colore: per le fasce zone usa un mapping coerente con il significato.
            const colorByZone = {
              MATTINA_8_12: "#0D9488",
              SEMPRE_APERTO: "#1B5E20",
              POMERIGGIO_14_18: "#1565C0",
              ORARIO_SU_APPUNTAMENTO: "#F57C00",
              DA_VERIFICARE: "#C62828",
            };
            const fascColor = colorByZone[f.fascia] || FASCE_COLOR[f.fascia] || "#64748b";
            const isAppuntamentoFascia = f.fascia === "APPUNTAMENTO" || f.fascia === "ORARIO_SU_APPUNTAMENTO";
            const startIdx = groupedFasce.slice(0, fi).reduce((acc, x) => acc + x.stopsResolved.length, 0);
            return (
              <div key={f.fascia} className="space-y-2">
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider"
                     style={{ backgroundColor: `${fascColor}15`, color: fascColor }}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: fascColor }} />
                  {fascLabel} — {f.stopsResolved.length} fermate
                  {isAppuntamentoFascia && (
                    <span className="ml-auto flex items-center gap-1 normal-case font-medium text-[10px] text-[#F57C00]">
                      <AlertTriangle size={11} /> da chiamare
                    </span>
                  )}
                </div>
                <SortableContext items={f.stopsResolved.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {f.stopsResolved.map((s, i) => (
                      <SortableStop
                        key={s.id}
                        stop={s}
                        index={startIdx + i}
                        color={color}
                        onRegister={onRegister}
                        onSkip={onSkip}
                        isDone={doneIds.has(s.id)}
                        onStopOverride={onStopOverride}
                        dataGiro={dataGiro}
                        currentMezzoId={mezzo?.id}
                        allMezzi={allMezzi}
                        isCarrier={isCarrier}
                        disableReorder={disableReorder}
                      />
                    ))}
                  </div>
                </SortableContext>
              </div>
            );
          })}
        </DndContext>
        {fermateSaltate.length > 0 && (
          <div className="space-y-2 pt-2 border-t border-dashed border-[#F57C00]/40">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-[#F57C00]/10 text-[#F57C00]">
              <SkipForward size={11} /> Fermate saltate — {fermateSaltate.length}
            </div>
            {fermateSaltate.map((s) => (
              <SkippedStop
                key={s.id}
                stop={s}
                onRestore={onRestore}
                onCreaRecupero={onCreaRecuperoSaltata}
                giroChiuso={chiuso}
                isCarrier={isCarrier}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
